/**
 * AuthContext — wraps Amplify's email/password sign-in for app-web (WS-D D2′).
 *
 * Login-only: the demo/dev site uses admin-provisioned accounts (permanent
 * passwords), so the surface is just `signIn` / `signOut`. No sign-up / verify /
 * forgot-reset (parked for prod — re-port from world-cup-v2; see bento-planning
 * first-demo-plan §7).
 *
 * Identity is read straight from the Cognito **ID token** in the current Amplify
 * session (`email` + `sub`) — there is NO `/me` call and no server user record
 * for identity. The ID token is also what `apiGet` attaches (see `data/api.ts`):
 * its `aud` = the app-client id, exactly what the D3 JWT authorizer validates,
 * and it carries the email this context needs.
 */

import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  fetchAuthSession,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { AMPLIFY_CONFIGURED } from "../lib/amplify";

/** The signed-in user, as read from the ID-token claims. No server record. */
export interface AuthUser {
  /** Cognito `sub` — stable user id. */
  userId: string;
  email: string;
}

interface AuthState {
  /** Null while loading and for anonymous (not-signed-in) callers. */
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Read {userId, email} from the current session's ID token, or null if none. */
async function readSessionUser(): Promise<AuthUser | null> {
  const session = await fetchAuthSession();
  const claims = session.tokens?.idToken?.payload;
  if (!claims) return null;
  const sub = typeof claims.sub === "string" ? claims.sub : null;
  const email = typeof claims.email === "string" ? claims.email : null;
  if (!sub || !email) return null;
  return { userId: sub, email };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      setUser(await readSessionUser());
    } catch {
      // fetchAuthSession throws when there's no session — treated as anonymous.
      setUser(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!AMPLIFY_CONFIGURED) {
        setLoading(false);
        return;
      }
      try {
        const me = await readSessionUser();
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep `user` in step with Amplify's own auth events (token refresh, sign-out
  // in another tab). Cheap and keeps the gate honest without polling.
  useEffect(() => {
    if (!AMPLIFY_CONFIGURED) return;
    return Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedIn" || payload.event === "tokenRefresh") {
        void refresh();
      } else if (payload.event === "signedOut") {
        setUser(null);
      }
    });
  }, []);

  const signIn = async (email: string, password: string) => {
    await amplifySignIn({ username: email, password });
    await refresh();
  };

  const signOut = async () => {
    if (AMPLIFY_CONFIGURED) await amplifySignOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
